import { palette } from "lib/miolo";
import formatFinancialValueForChart from "components/company/financials/formatFinancialValueForChart";
import type { FundamentalsLine } from "components/fundamentals/FundamentalsChart";
import FundamentalsChart from "components/fundamentals/FundamentalsChart";
import FundamentalsChartBlankslate from "components/fundamentals/FundamentalsChartBlankslate";
import FundamentalsChartBoxHeader from "components/fundamentals/FundamentalsChartBoxHeader";
import FundamentalsChartContext from "components/fundamentals/FundamentalsChartContext";
import FundamentalsChartContextProvider from "components/fundamentals/FundamentalsChartContextProvider";
import { Fundaments } from "components/fundamentals/Fundaments";
import { parse } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";
import type { IBrazilianRealEstateFundTopLevelFragment } from "lib/fragments/__generated__/BrazilianRealEstateFundTopLevel.generated";
import type { ReactElement } from "react";
import FundamentalsToggles from "./FundamentalsToggles";

type TFinancialStatementRow = IBrazilianRealEstateFundTopLevelFragment["financials"]["monthly"][0];

interface IBrazilianRealEstateFundFundamentalsChartProps {
  financials: TFinancialStatementRow[];
}

export default function BrazilianRealEstateFundFundamentalsChart({
  financials,
}: IBrazilianRealEstateFundFundamentalsChartProps): ReactElement {
  return (
    <FundamentalsChartContextProvider
      fundaments={[
        Fundaments.CAPITAL_WITH_LIQUIDITY,
        Fundaments.TOTAL_VALUE_OF_REAL_ESTATE,
        Fundaments.TOTAL_OF_OTHER_INVESTMENTS,
        Fundaments.TOTAL_OF_FUNDS_OF_FUNDS,
        Fundaments.TOTAL_FROM_RENTS,
        Fundaments.TOTAL_ASSETS,
        Fundaments.DISTRIBUTED_INCOME,
        Fundaments.PERFORMANCE_AND_ADMINISTRATION_TAXES,
        Fundaments.TOTAL_LIABILITIES,
        Fundaments.NET_EQUITY,
        Fundaments.EQUITY_VALUE_PER_QUOTA,
        Fundaments.YIELD_PER_QUOTA,
      ]}
    >
      <div
        css={{
          display: "grid",
          gridTemplateColumns: "repeat(12, 1fr)",
          gridColumnGap: 30,
          alignItems: "center",
          margin: "0px auto",
          width: 1140,
        }}
      >
        <div
          css={{
            gridColumn: "span 12",
            boxShadow: "0 4px 6px 0 #33333333",
            backgroundColor: "#ffffff",
            borderRadius: 4,
          }}
        >
          <FundamentalsChartContext.Consumer>
            {({ fundaments }) => (
              <FundamentalsChartBoxHeader title="Gráfico Fundamentalista" grayedOut={fundaments.length === 0} />
            )}
          </FundamentalsChartContext.Consumer>

          <div
            css={{
              padding: "24px 0px",
            }}
          >
            <div
              css={{
                display: "grid",
                gridRowGap: 16,
                padding: "0px 24px 0px 24px",
              }}
            >
              <div
                css={{
                  display: "grid",
                  gridColumnGap: 8,
                  gridAutoFlow: "column",
                  gridAutoColumns: "max-content",
                }}
              >
                <span
                  css={{
                    fontWeight: 500,
                    color: palette.baliHai,
                    fontSize: 14,
                    lineHeight: "18px",
                    letterSpacing: 0.6,
                  }}
                >
                  Indicadores
                </span>

                <FundamentalsChartContext.Consumer>
                  {({ selectAll, isAllSelected }) => {
                    return isAllSelected ? null : (
                      <button
                        type="button"
                        onClick={selectAll}
                        css={{
                          border: "none",
                          cursor: "pointer",
                          outline: 0,
                          padding: 0,
                          background: "transparent",
                          fontWeight: 600,
                          color: palette.royalBlue,
                          fontSize: 14,
                          lineHeight: "18px",
                          letterSpacing: 0.6,
                        }}
                      >
                        Mostrar Todos
                      </button>
                    );
                  }}
                </FundamentalsChartContext.Consumer>

                <FundamentalsChartContext.Consumer>
                  {({ deselectAll, fundaments }) => {
                    return fundaments.length === 0 ? null : (
                      <button
                        type="button"
                        onClick={deselectAll}
                        css={{
                          border: "none",
                          cursor: "pointer",
                          outline: 0,
                          padding: 0,
                          background: "transparent",
                          fontWeight: 600,
                          color: palette.link,
                          fontSize: 14,
                          lineHeight: "18px",
                          letterSpacing: 0.6,
                        }}
                      >
                        Ocultar Todos
                      </button>
                    );
                  }}
                </FundamentalsChartContext.Consumer>
              </div>

              <div
                css={{
                  display: "flex",
                  flexWrap: "wrap",

                  "> button": {
                    marginRight: 8,
                    marginBottom: 8,
                  },
                }}
              >
                <FundamentalsToggles />
              </div>
            </div>

            <FundamentalsChartContext.Consumer>
              {({ fundaments }) => {
                if (fundaments.length === 0) {
                  return (
                    <div className="flex flex-col items-center space-y-4 text-center">
                      <div className="flex rounded-full bg-gray-50 p-12">
                        <FundamentalsChartBlankslate width={120} height={120} />
                      </div>

                      <p className="font-aeonik font-normal text-gray-600">
                        Selecione pelo menos um indicador para visualização em gráfico.
                      </p>
                    </div>
                  );
                }

                return (
                  <FundamentalsChart<TFinancialStatementRow[]>
                    chart={{
                      width: 1140,
                      height: 480,
                      margin: {
                        top: 24,
                        right: fundaments.length === 2 ? 56 : 28,
                        bottom: 24,
                        left: 56,
                      },
                      padding: {
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                      },
                    }}
                    lines={getChartLines(fundaments)}
                    getFiscalDate={({ fiscalDate }) => {
                      return parse(fiscalDate, "yyyy-MM-dd", new Date(), {
                        locale: ptBR,
                      });
                    }}
                    yAxes={fundaments.length === 2 ? "left-and-right" : "left-only"}
                    data={financials}
                    axisBottomDateFormat="MM/yy"
                  />
                );
              }}
            </FundamentalsChartContext.Consumer>
          </div>
        </div>
      </div>
    </FundamentalsChartContextProvider>
  );
}

function getChartLines(fundaments: Fundaments[]): FundamentalsLine<TFinancialStatementRow>[] {
  const lines: FundamentalsLine<TFinancialStatementRow>[] = [
    {
      key: Fundaments.CAPITAL_WITH_LIQUIDITY,
      label: "Caixa",
      value: (datum) => datum.capitalWithLiquidity,
      stroke: palette.kumera,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.TOTAL_VALUE_OF_REAL_ESTATE,
      label: "Imóveis",
      value: (datum) => datum.totalValueOfRealEstate,
      stroke: palette.oceanGreen,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.TOTAL_OF_FUNDS_OF_FUNDS,
      label: "Fundos",
      value: (datum) => datum.totalOfFundsOfFunds,
      stroke: palette.amazon,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.TOTAL_OF_OTHER_INVESTMENTS,
      label: "Papéis",
      value: (datum) => datum.totalOfOtherInvestments,
      stroke: palette.fruitSalad,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.TOTAL_FROM_RENTS,
      label: "Aluguéis",
      value: (datum) => datum.totalFromRents,
      stroke: palette.seaGreen,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.TOTAL_ASSETS,
      label: "Ativos",
      value: (datum) => datum.totalAssets,
      stroke: palette.scienceBlue,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.DISTRIBUTED_INCOME,
      label: "Rendim.",
      value: (datum) => datum.distributedIncome,
      stroke: palette.dixie,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.PERFORMANCE_AND_ADMINISTRATION_TAXES,
      label: "Taxas",
      value: (datum) => datum.performanceAndAdministrationTaxes,
      stroke: palette.mojo,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.TOTAL_LIABILITIES,
      label: "Passivos",
      value: (datum) => datum.totalLiabilities,
      stroke: palette.burntUmber,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.NET_EQUITY,
      label: "Pat. Líq.",
      value: (datum) => datum.netEquity,
      stroke: palette.royalBlue,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.EQUITY_VALUE_PER_QUOTA,
      label: "V.P.C.",
      value: (datum) => datum.equityValuePerQuota,
      stroke: palette.mineShaft,
      valueFormatter: (equityValuePerQuota) => equityValuePerQuota.toFixed(1).replace(".", ","),
    },
    {
      key: Fundaments.YIELD_PER_QUOTA,
      label: "Rend.",
      value: (datum) => datum.yieldPerQuota,
      stroke: palette.luxorGold,
      valueFormatter: (yieldPerQuota) => yieldPerQuota.toFixed(2).replace(".", ","),
    },
  ];

  return lines.filter((line) => {
    if (line.key) {
      return fundaments.includes(line.key as Fundaments);
    }

    return false;
  });
}
